import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, RefreshCw, Check, X, AlertTriangle, Loader2, Crop } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useApi } from "../lib/api";
import { quality, faceCount } from "../lib/imageQuality";
import { detectQuad, defaultQuad, warp, outSize } from "../lib/perspective";
import { CornerEditor } from "./CornerEditor";

export const MODES = {
  photo: { ratio: 35 / 45, out: 700, facing: "user", title: "Take passport photo", tip: "Face the camera against a plain light wall. Keep the head and shoulders inside the frame." },
  card: { ratio: 85.6 / 54, out: 1400, facing: "environment", title: "Photograph the card", tip: "Place the card on a dark, plain surface. It's fine if it's at an angle; it gets straightened. Avoid glare." },
  cheque: { ratio: 2.3, out: 1600, free: true, facing: "environment", title: "Photograph the cheque or passbook", tip: "Lay it flat in good light. It's fine if it's at an angle." },
};
const MSG = {
  NotAllowedError: "Camera permission was blocked. Allow camera access in the browser, or use Upload instead.",
  NotFoundError: "No camera was found on this device. Use Upload instead.",
  NotReadableError: "The camera is being used by another app. Close it and try again, or use Upload instead.",
  OverconstrainedError: "This camera can't be used here. Try Switch camera, or use Upload instead.",
  default: "The camera could not be opened. Use Upload instead.",
};

const grab = (video, ratio, out, margin) => {
  const vw = video.videoWidth, vh = video.videoHeight;
  let h = vh * margin, w = h * ratio;
  if (w > vw * margin) { w = vw * margin; h = w / ratio; }
  const c = document.createElement("canvas");
  c.width = out; c.height = Math.round(out / ratio);
  c.getContext("2d").drawImage(video, (vw - w) / 2, (vh - h) / 2, w, h, 0, 0, c.width, c.height);
  return c;
};

const grabFull = (video, long = 1600) => {
  const vw = video.videoWidth, vh = video.videoHeight, k = Math.min(1, long / Math.max(vw, vh));
  const c = document.createElement("canvas");
  c.width = Math.round(vw * k); c.height = Math.round(vh * k);
  c.getContext("2d").drawImage(video, 0, 0, c.width, c.height);
  return c;
};

export const toFile = (canvas, name) => new Promise((res) => canvas.toBlob((b) => res(b && new File([b], `${name}-${Date.now()}.jpg`, { type: "image/jpeg" })), "image/jpeg", 0.88));

export const CameraCapture = ({ onPhoto, onClose, mode = "photo" }) => {
  const cfg = MODES[mode];
  const api = useApi();
  const video = useRef(null);
  const stream = useRef(null);
  const [facing, setFacing] = useState(cfg.facing);
  const [shot, setShot] = useState(null);
  const [issues, setIssues] = useState([]);
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState(null);
  const [raw, setRaw] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [aspect, setAspect] = useState(4 / 3);

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) { setErr("This browser cannot open the camera. Use Upload instead."); return undefined; }
    let live = true;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false })
      .then((s) => {
        if (!live) { s.getTracks().forEach((t) => t.stop()); return; }
        stream.current = s;
        if (video.current) { video.current.srcObject = s; video.current.play().catch(() => {}); }
      })
      .catch((e) => setErr(MSG[e?.name] || MSG.default));
    return () => { live = false; stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null; };
  }, [facing]);

  const checkFace = async (canvas) => {
    const local = await faceCount(canvas);
    if (local !== null) return local === 1 ? null : local === 0 ? "No face was found. Make sure the face is clear and inside the frame." : "More than one face is in the photo.";
    const file = await toFile(canvas, "check");
    const fd = new FormData(); fd.append("file", file);
    try { const r = (await api.post("/kyc/photo-check", fd)).data; return r.ok ? null : r.reason; } catch { return null; }
  };

  const review = async (c, auto) => {
    setShot({ canvas: c, url: c.toDataURL("image/jpeg", 0.85), auto });
    const q = quality(c);
    setIssues([q.dark && "The photo is too dark. Move to better light.", q.bright && "The photo is too bright or has glare.", q.blurry && "The photo looks blurry. Hold the phone steady and tap to focus."].filter(Boolean));
    if (mode === "photo") {
      setChecking(true);
      const f = await checkFace(c);
      setIssues((x) => (f ? [...x, f] : x));
      setChecking(false);
    }
  };
  const take = () => {
    if (!video.current?.videoWidth) return;
    if (mode === "photo") return review(grab(video.current, cfg.ratio, cfg.out, 0.9), false);
    const c = grabFull(video.current);
    const q = detectQuad(c);
    setRaw({ canvas: c, url: c.toDataURL("image/jpeg", 0.85), quad: q || defaultQuad(c), auto: !!q });
    setAdjusting(true);
  };
  const straighten = () => {
    const [w, h] = outSize(raw.quad, cfg.out, cfg.free ? null : cfg.ratio);
    setAdjusting(false);
    review(warp(raw.canvas, raw.quad, w, h), raw.auto);
  };
  const use = async () => {
    const file = await toFile(shot.canvas, mode === "photo" ? "passport-photo" : mode);
    if (!file) return toast.error("Could not save the photo");
    onPhoto(file); onClose();
  };
  const retake = () => { setShot(null); setIssues([]); setRaw(null); setAdjusting(false); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121824] border-[#1F2A3E] max-w-md" data-testid="camera-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">{cfg.title}</DialogTitle>
          <p className="text-xs text-slate-400">{cfg.tip}</p></DialogHeader>
        {err ? <p className="text-sm text-rose-400" data-testid="camera-error">{err}</p> : (
          <div className={`relative mx-auto w-full overflow-hidden rounded-lg bg-black ${adjusting ? "hidden" : ""}`} style={{ aspectRatio: mode === "photo" ? "35 / 45" : `${shot ? shot.canvas.width / shot.canvas.height : aspect}`, maxWidth: mode === "photo" ? 300 : 420 }}>
            <video ref={video} playsInline muted onLoadedMetadata={(e) => e.target.videoHeight && setAspect(e.target.videoWidth / e.target.videoHeight)} className={`absolute inset-0 w-full h-full object-cover ${facing === "user" ? "-scale-x-100" : ""} ${shot ? "invisible" : ""}`} data-testid="camera-video" />
            {!shot && <div className={`absolute ${mode === "photo" ? "inset-[6%] rounded-[45%_45%_12%_12%/40%_40%_10%_10%]" : "inset-[10%] rounded-md"} border-2 border-dashed border-white/70 pointer-events-none`} />}
            {shot && <img src={shot.url} alt="Captured" className="absolute inset-0 w-full h-full object-contain bg-black" data-testid="camera-preview" />}
          </div>)}
        {adjusting && raw && <>
          <CornerEditor url={raw.url} width={raw.canvas.width} height={raw.canvas.height} quad={raw.quad} setQuad={(quad) => setRaw((r) => ({ ...r, quad }))} />
          <p className={`text-[11px] text-center ${raw.auto ? "text-slate-400" : "text-amber-300"}`} data-testid="camera-corner-note">
            {raw.auto ? "Corners found automatically. Drag any green dot onto the card's corner if needed." : "Corners not found. Drag the 4 green dots onto the card's corners."}</p></>}
        {shot && mode !== "photo" && <p className="text-[11px] text-slate-500 text-center" data-testid="camera-crop-note">Straightened to a flat copy.</p>}
        {checking && <p className="text-xs text-slate-400 flex items-center gap-2 justify-center" data-testid="camera-checking"><Loader2 size={12} className="animate-spin" />Checking the photo…</p>}
        {!checking && issues.length > 0 && <div className="rounded-md border border-amber-400/30 bg-amber-400/10 p-3 grid gap-1" data-testid="camera-issues">
          {issues.map((i) => <p key={i} className="text-xs text-amber-200 flex items-start gap-1.5"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{i}</p>)}</div>}
        <div className="flex flex-wrap gap-2 justify-center">
          {adjusting && <>
            <Button onClick={straighten} className="bg-[#00E599] text-[#0B1017] font-semibold" data-testid="camera-straighten-btn"><Crop size={14} className="mr-1.5" />Straighten</Button>
            <Button variant="outline" onClick={retake} className="border-[#1F2A3E]" data-testid="camera-adjust-retake-btn"><X size={14} className="mr-1.5" />Retake</Button>
          </>}
          {!err && !shot && !adjusting && <>
            <Button onClick={take} className="bg-[#00E599] text-[#0B1017] font-semibold" data-testid="camera-capture-btn"><Camera size={15} className="mr-1.5" />Capture</Button>
            <Button variant="outline" onClick={() => setFacing(facing === "user" ? "environment" : "user")} className="border-[#1F2A3E]" data-testid="camera-switch-btn"><RefreshCw size={14} className="mr-1.5" />Switch camera</Button>
          </>}
          {shot && <>
            <Button onClick={issues.length ? retake : use} disabled={checking} className="bg-[#00E599] text-[#0B1017] font-semibold" data-testid={issues.length ? "camera-retake-primary-btn" : "camera-use-btn"}>
              {issues.length ? <><RefreshCw size={14} className="mr-1.5" />Retake</> : <><Check size={15} className="mr-1.5" />Use photo</>}</Button>
            {issues.length > 0 ? <Button variant="outline" disabled={checking} onClick={use} className="border-[#1F2A3E]" data-testid="camera-use-anyway-btn">Use anyway</Button>
              : <Button variant="outline" onClick={retake} className="border-[#1F2A3E]" data-testid="camera-retake-btn"><X size={14} className="mr-1.5" />Retake</Button>}
            {raw && <Button variant="outline" onClick={() => { setShot(null); setIssues([]); setAdjusting(true); }} className="border-[#1F2A3E]" data-testid="camera-adjust-btn"><Crop size={14} className="mr-1.5" />Adjust corners</Button>}
          </>}
        </div>
      </DialogContent>
    </Dialog>);
};
